import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { transitionState } from "./PageTransition";
import { isSectionRoot } from "./activePath";
import { useNavigationGuard } from "./UnsavedChanges";
import { ArrowLeftIcon } from "../ui/Icons";
import { ROUTES } from "../../routes/paths";

/** One step of the trail. The page you are on carries no link. */
export interface Crumb {
  label: string;
  /** Where the step goes. Left off for the current page. */
  to?: string;
}

interface PageHeaderProps {
  /**
   * What the page is called. A node rather than a string only because one
   * screen makes its title editable in place — see `PlanNameField`; every
   * other one passes the text.
   */
  title: React.ReactNode;
  /**
   * Set beside the title, inside the same heading — the plan number after a
   * plan's name. Part of the heading rather than a line under it, because it
   * is part of what the page is called.
   */
  titleSuffix?: React.ReactNode;
  /**
   * The trail back to where this screen was opened from, shown under the
   * title. Only screens that sit under another one pass it — on a section's
   * own page the trail would say no more than the title already does.
   */
  breadcrumbs?: Crumb[];
  /**
   * Buttons set against the right edge, level with the title and the trail
   * under it — an action on the record the whole screen is about rather than on
   * one tab of it.
   *
   * Actions that belong to what a single tab is showing stay in that tab.
   */
  actions?: React.ReactNode;
  /**
   * Where the phone's back arrow goes, for a screen whose way out is not the
   * step above it in the trail — or `false` on one that should carry no arrow
   * at all.
   *
   * Left off almost everywhere: the arrow works the trail out for itself, and
   * falls back to the history when a screen has none. Pass it only when the
   * screen knows better than either.
   */
  back?: string | false;
}

/**
 * Whether the browser has an entry under this one to go back to.
 *
 * Read off the router's own numbering rather than `history.length`, which
 * counts the entries ahead as well and says a deep link can go back when it
 * cannot. An entry with no number under it — a memory router, or a history
 * this tab did not write — answers no, and the arrow then needs a trail to
 * stand on.
 */
const hasHistoryBelow = (): boolean => {
  const index = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof index === "number" && index > 0;
};

/** Title row shared by every signed-in screen. */
const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  titleSuffix,
  breadcrumbs,
  actions,
  back,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  // The same guard the rail and the tab bar leave by, so the arrow cannot walk
  // out of a half-filled form without asking — see `UnsavedChanges`.
  const guardNavigation = useNavigationGuard();

  /**
   * The screen above this one: what the screen itself named, else the last
   * step of the trail that leads anywhere, which is the parent by definition.
   */
  const parent =
    back !== undefined
      ? back
      : [...(breadcrumbs ?? [])].reverse().find((crumb) => crumb.to)?.to;

  /**
   * No arrow on the screens the tab bar goes to: a phone starts on those, and
   * an arrow there either leaves the app or walks back into whatever was open
   * before it, which is not "up". Everywhere else was opened from somewhere,
   * and carries one — unconditionally, which is the part worth saying.
   *
   * It used to need somewhere to go before it would be drawn: a trail, or an
   * entry underneath. That was safe while the tab bar was under every screen,
   * because a screen with neither still had three sections across its foot to
   * leave by. The bar is the sections' own now — see `barShown` in `AppLayout`
   * — so a screen with no arrow is a screen with no way out at all, and the
   * one case that produced is the one most likely to be met by somebody who
   * did not come from inside the app: a bookmark, a pasted link or a new tab
   * at `/settings?tab=billings`, `/revenue`, `/my-brand`, `/dishes`. Those
   * screens are opened from the account menu and name no parent, so there was
   * nothing for the old rule to find. `goBack` below is what answers for them.
   *
   * A screen that names its own way back outranks all of it, because naming
   * one is the screen saying it knows better — My task is a section by its URL
   * and by the circle that opens it, and it takes the foot of the phone for
   * its own day, so the bar that would otherwise be the way out is not drawn
   * while it is open. `back={false}` is the other end of the same prop, for a
   * screen that means to carry none.
   */
  const showBack =
    back !== false &&
    (typeof back === "string" || !isSectionRoot(location.pathname));

  const goBack = () =>
    guardNavigation(() => {
      if (typeof parent === "string") {
        // A step of the trail is always a way back up it, whatever the two
        // paths look like — the same reason the crumbs below carry it.
        navigate(parent, { state: transitionState("back") });
        return;
      }
      if (hasHistoryBelow()) {
        navigate(-1);
        return;
      }
      // Nothing named and nothing behind: home. It is where a phone goes to
      // leave the screen it is on — the same place My task's own arrow is
      // pointed, and the one screen in the app that is always somewhere to
      // arrive rather than somewhere to come back out of. A `replace` would
      // be wrong even though this entry has nothing under it: Back from the
      // dashboard should leave the app the way it did before, not land on
      // the screen the reader has just chosen to leave.
      navigate(ROUTES.dashboard, { state: transitionState("back") });
    });

  return (
    // A row only when there are actions to set opposite the title; without them
    // the header is what it always was, so nothing else on the app changes shape.
    <header className="flex-shrink-0 min-w-0 flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
      <div className="min-w-0 flex-1 flex items-start gap-1">
        {/* The phone's way back, level with the title it belongs to rather than
          in the bar above — the arrow and the name of the screen it is leaving
          are one sentence, and split across two rows they read as two.

          A phone only. On a wide screen the rail is always open and the trail
          under the title is already the way up; a second one would be a third
          way to leave the same screen.

          40px of touch target with the height it would have added taken back
          out, so the arrow sits on the title's line and not above it. */}
        {showBack && (
          <button
            type="button"
            onClick={goBack}
            aria-label={
              typeof parent === "string" && breadcrumbs?.length
                ? `Back to ${
                    [...breadcrumbs].reverse().find((crumb) => crumb.to)?.label
                  }`
                : "Go back"
            }
            className="md:hidden -ml-2.5 -my-2 h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-full text-ink transition-colors hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2CB]/40"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <h1 className="flex min-w-0 items-baseline gap-2 text-[18px] sm:text-[20px] 3xl:text-[24px] font-medium text-ink">
            <span className="truncate">{title}</span>
            {titleSuffix}
          </h1>
          {breadcrumbs && breadcrumbs.length > 0 && (
            <nav aria-label="Breadcrumb" className="mt-1">
              <ol className="flex flex-wrap items-center gap-1 text-[12px] 3xl:text-[13px] text-[#80828D]">
                {breadcrumbs.map((crumb, index) => (
                  <li key={crumb.label} className="flex items-center gap-1">
                    {crumb.to ? (
                      <Link
                        to={crumb.to}
                        // A step of the trail is always a way back up it, whatever
                        // the two paths look like — see `pageDirection`.
                        state={transitionState("back")}
                        className="hover:text-brand transition-colors"
                      >
                        {crumb.label}
                      </Link>
                    ) : (
                      // The last step names the page you are already on, so it is
                      // read out as the current one rather than offered as a way
                      // to somewhere else.
                      <span aria-current="page">{crumb.label}</span>
                    )}
                    {index < breadcrumbs.length - 1 && (
                      <span aria-hidden="true">&gt;</span>
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          )}
        </div>
      </div>

      {/* The screen's own actions, set against the right edge.

          The way into the account menu used to sit past them, on every
          screen that heads itself with a title. It is the home screen's
          alone now: home is where a phone goes to leave the screen it is
          on, and a second way out offered from inside one record — beside
          the arrow that is the way out — was the app asking to be left at
          every step of it. See `MobileWelcome`, which draws the button on
          the greeting's own line. */}
      {actions && (
        <div className="flex min-w-0 flex-shrink-0 flex-wrap items-center gap-2 sm:gap-3">
          {actions}
        </div>
      )}
    </header>
  );
};

export default PageHeader;
